import { Button, HStack, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router';

import useVersionInfo from 'hooks/useVersionInfo';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { useCompare } from './CompareContext';
import { CompareDatasetCard, CompareDatasetPlaceholder } from './CompareDatasetCard';
import { buildComparePath } from './compareUrl';
import { MAX_COMPARE_DATASETS } from './constants';
import { datasetRefKey, isSameSystemModel } from './datasetRef';
import { CompareDataset } from './types';
import { useIsDatasetComparisonEnabled } from './useIsDatasetComparisonEnabled';
import { compareEventProps } from './utils';

const CompareTray = () => {
  const { datasets, removeDataset, canCompare, getSelectionAgeSeconds } = useCompare();
  const { ver, system_model } = useVersionInfo();
  const navigate = useNavigate();
  const isEnabled = useIsDatasetComparisonEnabled();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const warningColor = useColorModeValue('orange.600', 'orange.300');

  if (!isEnabled || datasets.length === 0) return null;

  const slots = Array.from({ length: MAX_COMPARE_DATASETS }, (_, index) => datasets[index] ?? null);

  // Comparing across versions is a real question ("what changed between releases?"), but cutoff vs
  // consequential is methodologically apples-to-oranges — so that combination is refused here, where
  // a card can still be swapped out, rather than on the comparison page.
  const hasMixedSystemModels = datasets.some((dataset) => !isSameSystemModel(dataset, datasets[0]));
  const isBlocked = hasMixedSystemModels;

  const onRemove = (dataset: CompareDataset) => {
    mixpanel.track(MIXPANEL_EVENTS.COMPARE_DATASET_REMOVED, {
      dataset_id: dataset.id,
      dataset_version: dataset.version,
      dataset_system_model: dataset.systemModel,
      source: 'tray',
      selection_count: datasets.length - 1,
    });
    removeDataset(dataset);
  };

  const onCompare = () => {
    if (isBlocked) {
      mixpanel.track(MIXPANEL_EVENTS.COMPARISON_BLOCKED, {
        ...compareEventProps(datasets),
        reason: 'system_model_mismatch',
      });
      return;
    }

    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_STARTED, {
      ...compareEventProps(datasets),
      seconds_to_start: getSelectionAgeSeconds(),
    });
    navigate(buildComparePath('impact-assessment', { version: ver, system_model }, datasets), {
      state: { fromTray: true },
    });
  };

  return (
    <Stack
      as="section"
      aria-label="Datasets to compare"
      position="sticky"
      bottom={0}
      zIndex="sticky"
      bg={bg}
      borderTopWidth="1px"
      borderColor={borderColor}
      borderTopRadius="lg"
      boxShadow="0 -4px 16px rgba(0, 0, 0, 0.08)"
      direction={{ base: 'column', lg: 'row' }}
      align={{ base: 'stretch', lg: 'center' }}
      spacing={4}
      p={4}
    >
      <HStack flex={1} align="stretch" spacing={4} minW={0}>
        {slots.map((dataset, index) =>
          dataset ? (
            <CompareDatasetCard key={datasetRefKey(dataset)} dataset={dataset} onRemove={() => onRemove(dataset)} />
          ) : (
            <CompareDatasetPlaceholder key={`placeholder-${index}`} index={index + 1} />
          )
        )}
      </HStack>
      <Stack spacing={2} align={{ base: 'stretch', lg: 'center' }} alignSelf={{ base: 'stretch', lg: 'center' }}>
        <Button colorScheme="green" size="lg" px={10} isDisabled={!canCompare || isBlocked} onClick={onCompare}>
          Compare
        </Button>
        {hasMixedSystemModels && (
          <Text fontSize="xs" color={warningColor} maxW="220px" textAlign={{ base: 'left', lg: 'center' }}>
            Datasets must share a system model to be compared.
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

export default CompareTray;
